import React, {useMemo, useState} from 'react';
import {CButton, CInput} from '@coreui/react';
import CIcon from "@coreui/icons-react";
import {DragDropContext, Draggable, Droppable, DropResult} from "react-beautiful-dnd";
import {
    Etapa,
    Fluxo,
    Item,
    RegraInicio,
    Selecao,
    TipoItem,
    TipoItemList,
    TipoPapel,
    novaEtapa,
    novoItem,
    ordenaPorGrupo,
} from "./modelo";
import Painel from "./painel";

interface Props {
    fluxo: Fluxo,
    selecao: Selecao | null,
    somenteLeitura: boolean,
    onSelecionar: (selecao: Selecao | null) => void,
    onMudarEtapas: (etapas: Etapa[]) => void,
}

const ICONE_TIPO: {[tipo: number]: string} = {
    [TipoItem.CHECK]: 'cil-check-circle',
    [TipoItem.SELECAO]: 'cil-list',
    [TipoItem.SELECAO_MULTIPLA]: 'cil-list-rich',
    [TipoItem.TEXTO]: 'cil-notes',
    [TipoItem.ARQUIVO]: 'cil-paperclip',
    [TipoItem.DATA]: 'cil-calendar',
};

const SEPARADOR = '|';
const PREFIXO_ALVO = 'alvo-';
const PREFIXO_ETAPA = 'etapa-';
const droppableDoGrupo = (etapa: Etapa, grupo: string | null) => `${etapa._k}${SEPARADOR}${grupo ?? ''}`;
const normaliza = (texto: string) => texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Estrutura do fluxo em tres colunas: etapas (na ordem da sequencia), itens
 * da etapa aberta (por grupo) e o painel do que esta selecionado. Item
 * arrastado para uma etapa da coluna da esquerda muda de etapa.
 */
const Estrutura = ({fluxo, selecao, somenteLeitura, onSelecionar, onMudarEtapas}: Props) => {
    const [busca, setBusca] = useState('');
    // No celular o painel abre por cima; no desktop ele fica sempre na coluna.
    const [painelMovel, setPainelMovel] = useState(false);

    const etapa = fluxo.etapas.find(e => e._k === selecao?.etapa) ?? fluxo.etapas[0];
    const indiceEtapa = etapa ? fluxo.etapas.indexOf(etapa) : -1;

    const papeisCliente = useMemo(
        () => new Set(fluxo.papeis.filter(p => p.tipo === TipoPapel.CLIENTE).map(p => p.codigo)),
        [fluxo.papeis]
    );
    const nomeModulo = useMemo(() => {
        const mapa: {[codigo: string]: {nome: string, opcional: boolean}} = {};
        fluxo.modulos.forEach(m => { mapa[m.codigo] = {nome: m.nome, opcional: m.opcional}; });
        return mapa;
    }, [fluxo.modulos]);

    const termo = normaliza(busca.trim());
    const buscando = termo !== '';
    const arrastarDesligado = somenteLeitura || buscando;

    const seleciona = (nova: Selecao, abrirPainel = true) => {
        onSelecionar(nova);
        if (abrirPainel) {
            setPainelMovel(true);
        }
    };

    const abreEtapa = (alvo: Etapa) => {
        setBusca('');
        onSelecionar({etapa: alvo._k});
        setPainelMovel(false);
    };

    const adicionarEtapa = () => {
        const nova = novaEtapa(fluxo.papeis[0]?.codigo ?? '');
        onMudarEtapas([...fluxo.etapas, nova]);
        seleciona({etapa: nova._k});
    };

    const adicionarItem = (grupo: string | null) => {
        if (!etapa) {
            return;
        }
        const item = novoItem(etapa.papel, grupo);
        onMudarEtapas(fluxo.etapas.map(e => (e._k === etapa._k ? ordenaPorGrupo({...e, itens: [...e.itens, item]}) : e)));
        seleciona({etapa: etapa._k, item: item._k});
    };

    const adicionarGrupo = () => {
        if (!etapa) {
            return;
        }
        const usados = new Set(etapa.grupos.map(g => g.codigo));
        let n = etapa.grupos.length + 1;
        while (usados.has(`${indiceEtapa + 1}.${n}`)) {
            n++;
        }
        onMudarEtapas(fluxo.etapas.map(e => (e._k === etapa._k ? {...e, grupos: [...e.grupos, {codigo: `${indiceEtapa + 1}.${n}`, nome: 'Novo grupo'}]} : e)));
        seleciona({etapa: etapa._k});
    };

    const fimArraste = ({source, destination, type, draggableId}: DropResult) => {
        if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) {
            return;
        }
        if (type === 'ETAPA') {
            const etapas = [...fluxo.etapas];
            const [movida] = etapas.splice(source.index, 1);
            etapas.splice(destination.index, 0, movida);
            onMudarEtapas(etapas);
            return;
        }

        const origem = fluxo.etapas.find(e => e.itens.some(i => i._k === draggableId));
        const item = origem?.itens.find(i => i._k === draggableId);
        if (!origem || !item) {
            return;
        }

        // Soltou sobre uma etapa da coluna da esquerda: vai para o fim dela.
        if (destination.droppableId.startsWith(PREFIXO_ALVO)) {
            const destinoK = destination.droppableId.slice(PREFIXO_ALVO.length);
            const destino = fluxo.etapas.find(e => e._k === destinoK);
            if (!destino || destino._k === origem._k) {
                return;
            }
            const movido: Item = {
                ...item,
                grupo: destino.grupos.some(g => g.codigo === item.grupo) ? item.grupo : null,
                prazo_dias_uteis: Math.min(item.prazo_dias_uteis, destino.duracao_dias_uteis),
            };
            onMudarEtapas(fluxo.etapas.map(e => {
                if (e._k === origem._k) {
                    return {...e, itens: e.itens.filter(i => i._k !== item._k)};
                }
                if (e._k === destino._k) {
                    return ordenaPorGrupo({...e, itens: [...e.itens, movido]});
                }
                return e;
            }));
            if (selecao?.item === item._k) {
                onSelecionar({etapa: origem._k});
            }
            return;
        }

        // Dentro da etapa aberta: troca de grupo e/ou de posicao.
        const grupoDestino = destination.droppableId.split(SEPARADOR)[1] || null;
        const semItem = origem.itens.filter(i => i._k !== item._k);
        const vizinhos = semItem.filter(i => (i.grupo ?? '') === (grupoDestino ?? ''));
        const antesDe = vizinhos[destination.index];
        const ultimo = vizinhos[vizinhos.length - 1];
        const posicao = antesDe ? semItem.indexOf(antesDe) : (ultimo ? semItem.indexOf(ultimo) + 1 : semItem.length);
        semItem.splice(posicao, 0, {...item, grupo: grupoDestino});
        onMudarEtapas(fluxo.etapas.map(e => (e._k === origem._k ? ordenaPorGrupo({...e, itens: semItem}) : e)));
    };

    // ------------------------------------------------------------------ coluna das etapas

    const renderEtapaRail = (e: Etapa, i: number) => {
        const ativos = e.itens.filter(item => item.ativo).length;
        const paralela = i > 0 && e.regra_inicio === RegraInicio.JUNTO_ANTERIOR;
        const aberta = e._k === etapa?._k;
        return (
            <Draggable key={e._k} draggableId={PREFIXO_ETAPA + e._k} index={i} isDragDisabled={somenteLeitura}>
                {provided => (
                    <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                         className={`fx-etapa ${aberta ? 'aberta' : ''} ${paralela ? 'paralela' : ''} ${e.ativo ? '' : 'inativa'}`}>
                        <Droppable droppableId={PREFIXO_ALVO + e._k} type="ITEM" isDropDisabled={somenteLeitura || aberta}>
                            {(alvo, snapshot) => (
                                <div ref={alvo.innerRef} {...alvo.droppableProps}
                                     className={`fx-etapa-alvo ${snapshot.isDraggingOver ? 'sobre' : ''}`}
                                     onClick={() => abreEtapa(e)}>
                                    <span className="fx-etapa-numero">{i + 1}</span>
                                    <span className="fx-etapa-texto">
                                        <span className="fx-etapa-nome">{e.nome || <em>Etapa sem nome</em>}</span>
                                        <span className="fx-etapa-meta">
                                            {paralela && <span className="fx-paralela" title="Começa junto com a etapa anterior">‖ paralela · </span>}
                                            {e.duracao_dias_uteis} d.u. · {ativos} {ativos === 1 ? 'item' : 'itens'}
                                            {!e.ativo && ' · inativa'}
                                        </span>
                                    </span>
                                    <span className="fx-oculto">{alvo.placeholder}</span>
                                </div>
                            )}
                        </Droppable>
                    </div>
                )}
            </Draggable>
        );
    };

    // ------------------------------------------------------------------ lista de itens

    const renderItem = (dono: Etapa, item: Item, indice: number, mostrarEtapa = false) => {
        const modulo = item.modulo ? nomeModulo[item.modulo] : undefined;
        const doCliente = papeisCliente.has(item.papel) || !!item.acao_cliente;
        return (
            <Draggable key={item._k} draggableId={item._k} index={indice} isDragDisabled={arrastarDesligado}>
                {provided => (
                    <div ref={provided.innerRef} {...provided.draggableProps}
                         className={`fx-item ${selecao?.item === item._k ? 'selecionado' : ''} ${item.ativo ? '' : 'inativo'}`}
                         onClick={() => seleciona({etapa: dono._k, item: item._k})}>
                        <span className="fx-col-arrastar" {...provided.dragHandleProps} title="Arrastar: solte em outro grupo ou numa etapa à esquerda">
                            {!arrastarDesligado && <CIcon name="cil-cursor-move"/>}
                        </span>
                        <span className="fx-col-titulo">
                            <span className="fx-item-titulo">
                                <CIcon name={ICONE_TIPO[item.tipo] || 'cil-check-circle'} className="fx-item-icone"/>
                                {item.titulo || <em>Item sem título</em>}
                            </span>
                            <span className="fx-item-sub">
                                {modulo && (
                                    <span className={`fx-tag ${modulo.opcional ? 'opcional' : ''}`}
                                          title={modulo.opcional ? 'Módulo opcional: só entra se contratado' : 'Módulo'}>
                                        {modulo.nome}{modulo.opcional ? ' · opcional' : ''}
                                    </span>
                                )}
                                <span className="fx-item-sub-texto">{mostrarEtapa ? dono.nome : item.orientacao}</span>
                            </span>
                        </span>
                        <span className="fx-col-papel">
                            <span className={`fx-tag ${papeisCliente.has(item.papel) ? 'cliente' : 'papel'}`}>{item.papel}</span>
                        </span>
                        <span className="fx-col-tipo">{TipoItemList.getDescription(item.tipo)}</span>
                        <span className="fx-col-prazo" title="Prazo em dias úteis a partir do início da etapa">{item.prazo_dias_uteis}</span>
                        <span className="fx-col-marcas">
                            {doCliente && <CIcon name="cil-user" className="fx-marca-cliente"/>}
                            {!item.visivel_cliente && <CIcon name="cil-lock-locked" className="fx-marca-interno"/>}
                        </span>
                    </div>
                )}
            </Draggable>
        );
    };

    const renderGrupo = (aberta: Etapa, grupo: string | null, nome: string) => {
        const itens = aberta.itens.filter(item => (item.grupo ?? '') === (grupo ?? ''));
        return (
            <div className="fx-grupo" key={grupo ?? '__sem_grupo'}>
                <div className="fx-grupo-titulo">
                    <span>{grupo && <strong>{grupo}</strong>} {nome}</span>
                    <span className="fx-grupo-total">{itens.length}</span>
                    {!somenteLeitura && (
                        <button type="button" className="fx-link" onClick={() => adicionarItem(grupo)}>
                            <CIcon name="cil-plus"/> item
                        </button>
                    )}
                </div>
                <Droppable droppableId={droppableDoGrupo(aberta, grupo)} type="ITEM" isDropDisabled={arrastarDesligado}>
                    {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}
                             className={`fx-grupo-itens ${snapshot.isDraggingOver ? 'sobre' : ''}`}>
                            {itens.map((item, i) => renderItem(aberta, item, i))}
                            {provided.placeholder}
                            {itens.length === 0 && <div className="fx-vazio">Nenhum item. Arraste um item para cá ou use “+ item”.</div>}
                        </div>
                    )}
                </Droppable>
            </div>
        );
    };

    const resultadosBusca = useMemo(() => {
        if (!buscando) {
            return [];
        }
        const lista: Array<{dono: Etapa, item: Item}> = [];
        fluxo.etapas.forEach(dono => dono.itens.forEach(item => {
            if (normaliza(item.titulo).includes(termo) || normaliza(item.orientacao || '').includes(termo)) {
                lista.push({dono, item});
            }
        }));
        return lista;
    }, [buscando, termo, fluxo.etapas]);

    const cabecalhoColunas = (
        <div className="fx-colunas">
            <span className="fx-col-arrastar"/>
            <span className="fx-col-titulo">Atividade</span>
            <span className="fx-col-papel">Papel</span>
            <span className="fx-col-tipo">Resposta</span>
            <span className="fx-col-prazo" title="Prazo em dias úteis">Prazo</span>
            <span className="fx-col-marcas"/>
        </div>
    );

    const semGrupo = etapa ? etapa.itens.some(i => i.grupo === null) || etapa.grupos.length === 0 : false;

    return (
        <DragDropContext onDragEnd={fimArraste}>
            <div className={`fx-estrutura ${painelMovel ? 'painel-aberto' : ''}`}>
                <nav className="fx-rail">
                    <div className="fx-coluna-titulo">
                        Etapas <span className="fx-contador">{fluxo.etapas.length}</span>
                    </div>
                    <Droppable droppableId="etapas" type="ETAPA" isDropDisabled={somenteLeitura}>
                        {provided => (
                            <div ref={provided.innerRef} {...provided.droppableProps} className="fx-rail-lista">
                                {fluxo.etapas.map(renderEtapaRail)}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                    {!somenteLeitura && (
                        <button type="button" className="fx-rail-nova" onClick={adicionarEtapa} disabled={fluxo.papeis.length === 0}
                                title={fluxo.papeis.length === 0 ? 'Cadastre um papel primeiro' : ''}>
                            <CIcon name="cil-plus"/> Nova etapa
                        </button>
                    )}
                </nav>

                <section className="fx-lista">
                    {etapa ? (
                        <>
                            <div className="fx-lista-topo">
                                <div className="fx-lista-titulo">
                                    <span className="fx-etapa-numero grande">{indiceEtapa + 1}</span>
                                    <div>
                                        <h5>{etapa.nome || <em>Etapa sem nome</em>}</h5>
                                        <div className="fx-lista-meta">
                                            {etapa.duracao_dias_uteis} dias úteis
                                            {' · '}{indiceEtapa === 0 ? 'começa no início do processo'
                                                : etapa.regra_inicio === RegraInicio.JUNTO_ANTERIOR ? `junto com a etapa ${indiceEtapa}` : `após a etapa ${indiceEtapa}`}
                                            {' · responsável '}<strong>{etapa.papel}</strong>
                                        </div>
                                    </div>
                                </div>
                                <div className="fx-lista-acoes">
                                    <div className="fx-busca">
                                        <CIcon name="cil-search"/>
                                        <CInput size="sm" placeholder="Buscar em todas as etapas" value={busca} onChange={(e: any) => setBusca(e.target.value)}/>
                                    </div>
                                    <CButton size="sm" color="dark" variant="outline" onClick={() => seleciona({etapa: etapa._k})}>
                                        <CIcon name="cil-settings"/> Etapa
                                    </CButton>
                                    {!somenteLeitura && (
                                        <>
                                            <CButton size="sm" color="dark" variant="outline" onClick={adicionarGrupo}>
                                                <CIcon name="cil-plus"/> Grupo
                                            </CButton>
                                            <CButton size="sm" color="primary" onClick={() => adicionarItem(etapa.grupos[etapa.grupos.length - 1]?.codigo ?? null)}>
                                                <CIcon name="cil-plus"/> Item
                                            </CButton>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="fx-lista-corpo">
                                {cabecalhoColunas}
                                {buscando ? (
                                    <div className="fx-grupo">
                                        <div className="fx-grupo-titulo">
                                            <span>{resultadosBusca.length} {resultadosBusca.length === 1 ? 'item encontrado' : 'itens encontrados'}</span>
                                        </div>
                                        <Droppable droppableId="busca" type="ITEM" isDropDisabled>
                                            {provided => (
                                                <div ref={provided.innerRef} {...provided.droppableProps}>
                                                    {resultadosBusca.map(({dono, item}, i) => renderItem(dono, item, i, true))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </div>
                                ) : (
                                    <>
                                        {semGrupo && renderGrupo(etapa, null, etapa.grupos.length ? 'Sem grupo' : 'Itens')}
                                        {etapa.grupos.map(g => renderGrupo(etapa, g.codigo, g.nome))}
                                    </>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="fx-vazio grande">
                            <p>Este fluxo ainda não tem etapas.</p>
                            {!somenteLeitura && (
                                <CButton color="primary" onClick={adicionarEtapa} disabled={fluxo.papeis.length === 0}>
                                    <CIcon name="cil-plus"/> Criar a primeira etapa
                                </CButton>
                            )}
                        </div>
                    )}
                </section>

                {etapa && (
                    <Painel fluxo={fluxo}
                            selecao={selecao?.etapa === etapa._k || selecao?.item ? selecao! : {etapa: etapa._k}}
                            somenteLeitura={somenteLeitura}
                            onSelecionar={nova => onSelecionar(nova ?? {etapa: etapa._k})}
                            onFechar={() => setPainelMovel(false)}
                            onMudarEtapas={onMudarEtapas}/>
                )}
            </div>
        </DragDropContext>
    );
};

export default Estrutura;
